import { createReducer, on } from '@ngrx/store';

import * as TaskActions from './task.actions';
import type { TaskItem, TaskNotification, TaskProject, TaskSprint, TaskState } from './task.models';

export const initialState: TaskState = {
  projects: [],
  sprints: [],
  tasks: [],
  detailTask: null,
  notifications: [],
  loadingProjects: false,
  loadingSprints: false,
  loadingTasks: false,
  loadingDetail: false,
  loadingNotifications: false,
  saving: false,
  uploadingAttachment: false,
  error: null,
  message: null,
  success: false,
};

function upsertProject(projects: TaskProject[], project: TaskProject): TaskProject[] {
  const nextProjects = projects.filter((item) => item.id !== project.id);
  if (project.is_archived) {
    return nextProjects;
  }
  return [...nextProjects, project].sort((left, right) => left.name.localeCompare(right.name));
}

function upsertSprint(sprints: TaskSprint[], sprint: TaskSprint): TaskSprint[] {
  const nextSprints = sprints.filter((item) => item.id !== sprint.id);
  if (sprint.is_archived) {
    return nextSprints;
  }
  return [sprint, ...nextSprints].sort((left, right) => {
    if (left.is_active !== right.is_active) {
      return left.is_active ? -1 : 1;
    }
    return left.name.localeCompare(right.name);
  });
}

function upsertTask(tasks: TaskItem[], task: TaskItem): TaskItem[] {
  if (task.is_archived) {
    return tasks.filter((item) => item.id !== task.id);
  }
  const nextTasks = tasks.filter((item) => item.id !== task.id);
  return [task, ...nextTasks];
}

function applyTaskUpdate(state: TaskState, task: TaskItem) {
  return {
    ...state,
    tasks: upsertTask(state.tasks, task),
    detailTask: state.detailTask?.id === task.id ? task : state.detailTask,
    saving: false,
    uploadingAttachment: false,
    loadingDetail: false,
    error: null,
    success: true,
  };
}

function upsertNotification(notifications: TaskNotification[], notification: TaskNotification): TaskNotification[] {
  const nextNotifications = notifications.filter((item) => item.id !== notification.id);
  return [notification, ...nextNotifications].sort((left, right) => right.created_at.localeCompare(left.created_at));
}

export const taskReducer = createReducer(
  initialState,

  on(TaskActions.TaskClearStatus, (state) => ({
    ...state,
    error: null,
    message: null,
    success: false,
  })),

  on(TaskActions.TaskProjectsFetchRequest, (state) => ({
    ...state,
    loadingProjects: true,
    error: null,
  })),
  on(TaskActions.TaskProjectsFetchSuccess, (state, { projects }) => ({
    ...state,
    projects: projects.filter((project) => !project.is_archived),
    loadingProjects: false,
    error: null,
  })),
  on(TaskActions.TaskProjectsFetchFailure, (state, { error }) => ({
    ...state,
    loadingProjects: false,
    error,
  })),

  on(TaskActions.TaskSprintsFetchRequest, (state) => ({
    ...state,
    loadingSprints: true,
    error: null,
  })),
  on(TaskActions.TaskSprintsFetchSuccess, (state, { sprints }) => ({
    ...state,
    sprints: sprints.filter((sprint) => !sprint.is_archived),
    loadingSprints: false,
    error: null,
  })),
  on(TaskActions.TaskSprintsFetchFailure, (state, { error }) => ({
    ...state,
    loadingSprints: false,
    error,
  })),

  on(TaskActions.TaskProjectCreateRequest, TaskActions.TaskProjectUpdateRequest, (state) => ({
    ...state,
    saving: true,
    error: null,
    success: false,
  })),
  on(TaskActions.TaskSprintCreateRequest, TaskActions.TaskSprintUpdateRequest, (state) => ({
    ...state,
    saving: true,
    error: null,
    success: false,
  })),
  on(TaskActions.TaskProjectCreateSuccess, (state, { project, successMessage }) => ({
    ...state,
    projects: upsertProject(state.projects, project),
    saving: false,
    error: null,
    message: successMessage ?? null,
    success: true,
  })),
  on(TaskActions.TaskProjectUpdateSuccess, (state, { project, successMessage, silentSuccess }) => ({
    ...state,
    projects: upsertProject(state.projects, project),
    saving: false,
    error: null,
    message: silentSuccess ? null : (successMessage ?? null),
    success: !silentSuccess,
  })),
  on(TaskActions.TaskSprintCreateSuccess, (state, { sprint, successMessage }) => ({
    ...state,
    sprints: upsertSprint(state.sprints, sprint),
    saving: false,
    error: null,
    message: successMessage ?? null,
    success: true,
  })),
  on(TaskActions.TaskSprintUpdateSuccess, (state, { sprint, successMessage, silentSuccess }) => ({
    ...state,
    sprints: upsertSprint(state.sprints, sprint),
    saving: false,
    error: null,
    message: silentSuccess ? null : (successMessage ?? null),
    success: !silentSuccess,
  })),
  on(TaskActions.TaskProjectCreateFailure, TaskActions.TaskProjectUpdateFailure, (state, { error }) => ({
    ...state,
    saving: false,
    error,
    success: false,
  })),

  on(TaskActions.TaskItemsFetchRequest, (state) => ({
    ...state,
    loadingTasks: true,
    error: null,
  })),
  on(TaskActions.TaskItemsFetchSuccess, (state, { tasks }) => ({
    ...state,
    tasks,
    loadingTasks: false,
    error: null,
  })),
  on(TaskActions.TaskItemsFetchFailure, (state, { error }) => ({
    ...state,
    loadingTasks: false,
    error,
  })),

  on(TaskActions.TaskNotificationsFetchRequest, (state) => ({
    ...state,
    loadingNotifications: true,
    error: null,
  })),
  on(TaskActions.TaskNotificationsFetchSuccess, (state, { notifications }) => ({
    ...state,
    notifications,
    loadingNotifications: false,
    error: null,
  })),
  on(TaskActions.TaskNotificationsFetchFailure, (state, { error }) => ({
    ...state,
    loadingNotifications: false,
    error,
  })),
  on(TaskActions.TaskNotificationReceived, (state, { notification }) => ({
    ...state,
    notifications: upsertNotification(state.notifications, notification),
  })),
  on(TaskActions.TaskNotificationMarkReadSuccess, (state, { notification }) => ({
    ...state,
    notifications: upsertNotification(state.notifications, notification),
  })),
  on(TaskActions.TaskNotificationsMarkAllReadSuccess, (state) => ({
    ...state,
    notifications: state.notifications.map((item) => item.read_at ? item : { ...item, read_at: new Date().toISOString() }),
  })),
  on(TaskActions.TaskNotificationsClearSuccess, (state) => ({
    ...state,
    notifications: [],
  })),

  on(TaskActions.TaskDetailFetchRequest, (state) => ({
    ...state,
    loadingDetail: true,
    error: null,
  })),
  on(TaskActions.TaskDetailFetchSuccess, (state, { task }) => ({
    ...state,
    tasks: upsertTask(state.tasks, task),
    detailTask: task,
    loadingDetail: false,
    error: null,
  })),
  on(TaskActions.TaskDetailFetchFailure, (state, { error }) => ({
    ...state,
    loadingDetail: false,
    error,
  })),

  on(
    TaskActions.TaskCreateRequest,
    TaskActions.TaskUpdateRequest,
    TaskActions.TaskCommentCreateRequest,
    TaskActions.TaskSubtaskCreateRequest,
    TaskActions.TaskSubtaskUpdateRequest,
    TaskActions.TaskSubtaskDeleteRequest,
    TaskActions.TaskTimeEntryCreateRequest,
    TaskActions.TaskTimeEntryUpdateRequest,
    TaskActions.TaskTimeEntryDeleteRequest,
    TaskActions.TaskTimerStartRequest,
    TaskActions.TaskTimerStopRequest,
    (state) => ({
      ...state,
      saving: true,
      error: null,
      success: false,
    }),
  ),
  on(TaskActions.TaskAttachmentUploadRequest, (state) => ({
    ...state,
    uploadingAttachment: true,
    error: null,
    success: false,
  })),

  on(TaskActions.TaskCreateSuccess, (state, { task, successMessage }) => ({
    ...state,
    tasks: upsertTask(state.tasks, task),
    detailTask: state.detailTask?.id === task.id ? task : state.detailTask,
    saving: false,
    error: null,
    message: successMessage ?? null,
    success: true,
  })),
  on(TaskActions.TaskUpdateSuccess, (state, { task, successMessage, silentSuccess }) => ({
    ...applyTaskUpdate(state, task),
    message: silentSuccess ? null : (successMessage ?? null),
    success: !silentSuccess,
  })),
  on(
    TaskActions.TaskCommentCreateSuccess,
    TaskActions.TaskSubtaskCreateSuccess,
    TaskActions.TaskSubtaskUpdateSuccess,
    TaskActions.TaskSubtaskDeleteSuccess,
    TaskActions.TaskTimeEntryCreateSuccess,
    TaskActions.TaskTimeEntryUpdateSuccess,
    TaskActions.TaskTimeEntryDeleteSuccess,
    TaskActions.TaskTimerStartSuccess,
    TaskActions.TaskTimerStopSuccess,
    (state, { task }) => applyTaskUpdate(state, task),
  ),
  on(TaskActions.TaskAttachmentUploadSuccess, (state, { response }) => ({
    ...applyTaskUpdate(state, response.task),
    uploadingAttachment: false,
  })),
  on(
    TaskActions.TaskCreateFailure,
    TaskActions.TaskUpdateFailure,
    TaskActions.TaskCommentCreateFailure,
    TaskActions.TaskSubtaskCreateFailure,
    TaskActions.TaskSubtaskUpdateFailure,
    TaskActions.TaskSubtaskDeleteFailure,
    TaskActions.TaskAttachmentUploadFailure,
    TaskActions.TaskTimeEntryCreateFailure,
    TaskActions.TaskTimeEntryUpdateFailure,
    TaskActions.TaskTimeEntryDeleteFailure,
    TaskActions.TaskTimerStartFailure,
    TaskActions.TaskTimerStopFailure,
    TaskActions.TaskSprintCreateFailure,
    TaskActions.TaskSprintUpdateFailure,
    TaskActions.TaskNotificationsFetchFailure,
    TaskActions.TaskNotificationMarkReadFailure,
    TaskActions.TaskNotificationsMarkAllReadFailure,
    TaskActions.TaskNotificationsClearFailure,
    (state, { error }) => ({
      ...state,
      saving: false,
      uploadingAttachment: false,
      loadingSprints: false,
      loadingNotifications: false,
      error,
      success: false,
    }),
  ),
);
